import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, Search, BookOpen, ExternalLink } from "lucide-react";
import {
  GLOSSARY,
  GLOSSARY_CATEGORIES,
  searchGlossary,
} from "@/lib/glossary";

/**
 * GlossaryDialog
 * ------------------------------------------------------------------
 * Quick-lookup searchable dialog reachable from the TopBar `?` button.
 * For a deeper experience, users can navigate to /glossary.
 */
const GlossaryDialog = ({ trigger, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  // Reset query when re-opening
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => searchGlossary(query), [query]);

  const grouped = useMemo(() => {
    const map = new Map();
    GLOSSARY_CATEGORIES.forEach((c) => map.set(c.key, []));
    filtered.forEach((t) => {
      if (!map.has(t.category)) map.set(t.category, []);
      map.get(t.category).push(t);
    });
    return map;
  }, [filtered]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open glossary"
            data-testid="topbar-help-button"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden"
        data-testid="glossary-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="flex items-center gap-2 font-display">
            <BookOpen className="h-5 w-5 text-[hsl(var(--primary))]" />
            Glossary &amp; Help
          </DialogTitle>
          <DialogDescription>
            Definitions of CSE and investment terms used across this app. Hover over any{" "}
            <span className="inline-flex items-center align-middle">
              <HelpCircle className="h-3.5 w-3.5 mx-0.5" />
            </span>{" "}
            icon to get a quick definition.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="glossary-search-input"
              placeholder="Search terms (e.g. 'yield on cost', 'PE', 'rebalancing')"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
        </div>

        <ScrollArea className="max-h-[55vh] px-6 py-4">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No terms match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            <div className="space-y-5">
              {GLOSSARY_CATEGORIES.map((cat) => {
                const terms = grouped.get(cat.key) || [];
                if (!terms.length) return null;
                return (
                  <section key={cat.key} data-testid={`glossary-section-${cat.key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="uppercase tracking-wider text-[10px]">
                        {cat.label}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {terms.length} term{terms.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {terms.map((t) => (
                        <div
                          key={t.id}
                          id={t.id}
                          data-testid={`glossary-term-${t.id}`}
                          className="rounded-md border border-border bg-card/40 p-3"
                        >
                          <div className="font-medium text-sm">{t.term}</div>
                          <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
                            {t.long}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="px-6 py-3 border-t border-border bg-muted/40">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            data-testid="glossary-dialog-close"
          >
            Close
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setOpen(false);
              navigate("/glossary");
            }}
            data-testid="glossary-dialog-open-page"
            className="gap-1.5"
          >
            Open full glossary page <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlossaryDialog;
